import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  inject,
  input,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
  signal,
  computed,
  effect,
  PLATFORM_ID,
} from '@angular/core';
import dayjs from 'dayjs';
import { timer, Subject, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { EventService } from 'api';

import { IconPipe } from 'common';
import { ButtonIconComponent } from 'ui-elements/button-icon';
import { SpinnerComponent } from 'ui-elements/spinner';
import { PopoverComponent } from 'ui-elements/popover';
import { SharedCalendarService } from '../services/shared-calendar.service';
import { rxResource } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ss-building-block-month-view-base',
  standalone: true,
  templateUrl: './month-view-base.component.html',
  styleUrls: ['./month-view-base.component.css'],
  imports: [
    CommonModule,
    ButtonIconComponent,
    IconPipe,
    SpinnerComponent,
    PopoverComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthViewBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  showCode = input<string>();
  theaterLocation = input<string>();

  @ViewChild('monthViewMain') monthViewMain!: ElementRef<HTMLElement>;
  @ViewChildren('calendarDay') calendarDays!: QueryList<
    ElementRef<HTMLElement>
  >;

  // Calendar data
  readonly months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ] as const;

  readonly dayHeaders = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ] as const;

  currMonth = signal<number>(dayjs().month());
  currYear = signal<number>(dayjs().year());
  lowerYear!: number;
  upperYear!: number;
  currentDay = dayjs().format('YYYY-MM-DD');
  // Use reactive calendar data from service
  calendar = computed(() => this.sharedCalendarService.calendarData());

  private readonly sharedCalendarService = inject(SharedCalendarService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly eventService = inject(EventService);

  isLoading = computed(() => this.sharedCalendarService.isLoading());
  showBottomGradient = signal(false);
  isMobile = signal(false);

  private readonly hasManuallyNavigated = signal(false);

  private readonly eventDetailsResource = rxResource({
    params: () => this.showCode(),
    stream: ({ params: showCode }) => {
      if (!showCode) {
        return of(null);
      }
      return this.eventService.getEventDetail(showCode).pipe(
        catchError((error) => {
          console.error('Failed to load event details:', error);
          return of(null);
        })
      );
    },
  });

  readonly eventDetails = computed(() => this.eventDetailsResource.value());
  readonly eventDetailsLoading = computed(() =>
    this.eventDetailsResource.isLoading()
  );

  eventStartMonth = computed(() => {
    const details = this.eventDetails();
    return details?.eventStartDate
      ? new Date(details.eventStartDate).getMonth()
      : null;
  });
  eventEndMonth = computed(() => {
    const details = this.eventDetails();
    return details?.eventEndDate
      ? new Date(details.eventEndDate).getMonth()
      : null;
  });
  eventStartYear = computed(() => {
    const details = this.eventDetails();
    return details?.eventStartDate
      ? new Date(details.eventStartDate).getFullYear()
      : null;
  });
  eventEndYear = computed(() => {
    const details = this.eventDetails();
    return details?.eventEndDate
      ? new Date(details.eventEndDate).getFullYear()
      : null;
  });

  isPreviousDisabled = computed(
    () =>
      this.eventStartMonth() !== null &&
      this.eventStartYear() !== null &&
      this.currMonth() === this.eventStartMonth() &&
      this.currYear() === this.eventStartYear()
  );

  isNextDisabled = computed(
    () =>
      this.eventEndMonth() !== null &&
      this.eventEndYear() !== null &&
      this.currMonth() === this.eventEndMonth() &&
      this.currYear() === this.eventEndYear()
  );

  private readonly destroy$ = new Subject<void>();

  constructor() {
    effect(() => {
      const showCode = this.showCode();
      if (showCode) {
        this.hasManuallyNavigated.set(false);
      }
    });

    effect(() => {
      const eventDetails = this.eventDetails();

      if (eventDetails?.eventStartDate && !this.hasManuallyNavigated()) {
        const eventStartDate = dayjs(eventDetails.eventStartDate);
        const today = dayjs();

        if (eventStartDate.isAfter(today, 'month')) {
          this.currMonth.set(eventStartDate.month());
          this.currYear.set(eventStartDate.year());
        } else {
          this.currMonth.set(today.month());
          this.currYear.set(today.year());
        }

        // Load calendar after event details and month are set
        this.loadCalendar();
      } else if (this.showCode() && !this.eventDetailsLoading()) {
        // Fallback to loading calendar even if no event details
        this.loadCalendar();
      }
    });

    // Effect to handle theaterLocation changes
    effect(() => {
      const theaterLocation = this.theaterLocation();
      const showCode = this.showCode();

      // Only load calendar for location changes if we have a showCode
      if (showCode && theaterLocation !== undefined) {
        this.loadCalendar();
      }
    });
  }

  ngOnInit() {
    const today = dayjs();
    this.currMonth.set(today.month());
    this.currYear.set(today.year());
    this.lowerYear = today.year();
    this.upperYear = today.year() + 1;

    // Check if mobile
    if (isPlatformBrowser(this.platformId)) {
      this.checkIfMobile();
      window.addEventListener('resize', () => this.checkIfMobile());
    }
  }

  private checkIfMobile() {
    this.isMobile.set(window.innerWidth < 768);
  }

  ngAfterViewInit() {
    if (this.showCode()) {
      this.loadCalendar();
    }
  }

  private loadCalendar() {
    const currMonth = this.currMonth();
    const currYear = this.currYear();

    if (currMonth === undefined || currYear === undefined) {
      return;
    }

    const eventLocation = this.eventDetails()?.theaterLocation;

    // Load calendar data using the reactive service
    this.sharedCalendarService.loadCalendarData({
      month: currMonth,
      year: currYear,
      showCode: this.showCode() || '',
      locationCD: eventLocation as any,
      options: { groupByWeeks: true },
    });

    // Scroll to current date on mobile after a short delay
    timer(200)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.scrollToCurrentDateOnMobile();
        this.initializeGradientState();
      });
  }

  private scrollToCurrentDateOnMobile() {
    if (!isPlatformBrowser(this.platformId) || window.innerWidth >= 768) {
      return;
    }

    // Check if ViewChildren are available
    if (!this.calendarDays || this.calendarDays.length === 0) {
      // Retry after a short delay if ViewChildren aren't ready
      timer(100)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.scrollToCurrentDateOnMobile();
        });
      return;
    }

    // Find the current date element first using ViewChildren
    let targetElement = this.calendarDays.find((dayRef) =>
      dayRef.nativeElement.classList.contains('today')
    )?.nativeElement;

    // If current date is not in this month, scroll to the first day of the current month instead
    if (!targetElement) {
      targetElement = this.calendarDays.find(
        (dayRef) =>
          !dayRef.nativeElement.classList.contains('pastmonth') &&
          !dayRef.nativeElement.classList.contains('nextmonth')
      )?.nativeElement;
    }

    if (targetElement && this.monthViewMain) {
      const scrollContainer = this.monthViewMain.nativeElement;

      // Calculate the position to scroll to
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = targetElement.getBoundingClientRect();
      const scrollTop =
        scrollContainer.scrollTop +
        elementRect.top -
        containerRect.top -
        containerRect.height / 2 +
        elementRect.height / 2;

      // Smooth scroll to the calculated position
      scrollContainer.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    }
  }

  onScroll(event: Event) {
    // Only handle scroll on mobile
    if (!isPlatformBrowser(this.platformId) || window.innerWidth >= 768) {
      return;
    }

    const scrollContainer = event.target as HTMLElement;
    this.checkScrollPosition(scrollContainer);
  }

  private initializeGradientState() {
    // Only initialize on mobile
    if (!isPlatformBrowser(this.platformId) || window.innerWidth >= 768) {
      return;
    }

    if (this.monthViewMain) {
      this.checkScrollPosition(this.monthViewMain.nativeElement);
    }
  }

  private checkScrollPosition(scrollContainer: HTMLElement) {
    const scrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;

    // If a user hasn't scrolled to the bottom of the element yet, show the gradient
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
    const shouldShowGradient = !isAtBottom && scrollHeight > clientHeight;

    if (this.showBottomGradient() !== shouldShowGradient) {
      this.showBottomGradient.set(shouldShowGradient);
    }
  }

  setPrevMonth() {
    if (this.isPreviousDisabled()) {
      return;
    }

    // Mark that user has manually navigated
    this.hasManuallyNavigated.set(true);

    if (this.currMonth() === 0) {
      this.currYear.update((year) => year - 1);
      this.currMonth.set(11);
    } else {
      this.currMonth.update((month) => month - 1);
    }
    this.loadCalendar();
  }

  setNextMonth() {
    if (this.isNextDisabled()) {
      return;
    }

    // Mark that user has manually navigated
    this.hasManuallyNavigated.set(true);

    if (this.currMonth() === 11) {
      this.currYear.update((year) => year + 1);
      this.currMonth.set(0);
    } else {
      this.currMonth.update((month) => month + 1);
    }
    this.loadCalendar();
  }

  onEventClick(
    clickEvent: Event,
    event: {
      eventStatusCode?: string;
      eventCode?: string;
      eventDateTime?: string;
    }
  ) {
    clickEvent.preventDefault();

    // Navigate to ticketing for regular events
    // Limited seating events (CO status) are handled by popover in template
    if (event.eventCode && event.eventDateTime) {
      this.router.navigate([
        '/ticketing',
        event.eventCode,
        event.eventDateTime,
      ]);
    }
  }

  getDayClasses(day: any): string {
    const classMap: Record<string, boolean> = {
      pastmonth: day.x === 'past',
      nextmonth: day.x === 'next',
      past: day.dateStr < this.currentDay,
      today: day.isToday,
      'other-month': !day.isCurrentMonth,
    };

    return Object.entries(classMap)
      .filter(([, condition]) => condition)
      .map(([className]) => className)
      .join(' ');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
